import { OButton, OInput, OResponsiveModal, OSwitch } from '@src/shared';

import questionConfigStyles from './questionConfigModal.module.scss';

import { TWebinarFormState } from '@src/webinar/types';

type TModalQuestionProps = {
  onQuestionsCancel: () => void;
  isQuestionsModalOpened: boolean;
  formState: any;
  updateFormField: <K extends keyof TWebinarFormState>(key: K, value: TWebinarFormState[K]) => void;
  isMaxLimitEnabled: boolean;
  handleMaxQuestionsToggle: (isOn: boolean) => void;
  removeFormError: (key: string) => void;
  setFormErrors: (data: any) => void;
  isMobile: boolean;
  onQuestionsDone: () => void;
  formErrors: { [key: string]: string };
};

const QuestionConfigModal = ({
  isQuestionsModalOpened,
  isMaxLimitEnabled,
  formState,
  isMobile,
  formErrors,
  onQuestionsCancel,
  updateFormField,
  handleMaxQuestionsToggle,
  removeFormError,
  setFormErrors,
  onQuestionsDone,
}: TModalQuestionProps) => {
  return (
    <OResponsiveModal
      isOpen={isQuestionsModalOpened}
      onClose={onQuestionsCancel}
      title="Q&A advanced settings"
      modalClasses={{
        container: questionConfigStyles.questionModalContainer,
        body: questionConfigStyles.questionModalBody,
      }}
      drawerActions={{
        onApply: onQuestionsDone,
        onCancel: onQuestionsCancel,
        applyLabel: 'Done',
        cancelLabel: 'Cancel',
      }}
      hasBackdrop={true}
    >
      <>
        <label htmlFor="allowAnonymous">Viewers </label>
        <OSwitch
          id="allowAnonymous"
          classes={{
            border: 'none',
            alignment: 'left',
            container: 'custom-background',
            label: questionConfigStyles.switchLabelContainer,
          }}
          label="Allow viewers to post questions anonymously"
          size="full"
          variant="primary"
          isChecked={formState.allowAnonymous}
          onChange={(checked) => updateFormField('allowAnonymous', checked)}
        />

        <div className={questionConfigStyles.maxQuestionsContainer}>
          <div className={questionConfigStyles.questionContainer}>
            <OSwitch
              classes={{
                border: 'none',
                alignment: 'left',
                container: 'custom-background',
                label: questionConfigStyles.switchLabelContainer,
              }}
              label="Define the max question limit per viewer"
              size="full"
              variant="primary"
              isChecked={isMaxLimitEnabled}
              onChange={(checked) => handleMaxQuestionsToggle(checked)}
            />

            <div className={questionConfigStyles.inputContainer}>
              <OInput
                type="number"
                className={`${questionConfigStyles.questionInput} ${!isMaxLimitEnabled ? `${questionConfigStyles.disabled}` : ''} ${formErrors.maxQuestions ? `${questionConfigStyles.invalid}` : ''}`}
                value={formState.maxQuestions}
                min={1}
                max={10}
                disabled={!formState.maxQuestions || !isMaxLimitEnabled || !formState.questionsEnabled}
                onChange={(e) => {
                  const value = Math.max(1, parseInt(e.target.value, 10) || 1); // avoid neg.

                  updateFormField('maxQuestions', value);

                  if (value <= 10) {
                    removeFormError('maxQuestions');
                  } else {
                    setFormErrors((errors: any) => ({
                      ...errors,
                      maxQuestions: 'The max allowed number of questions per viewer is 10 questions',
                    }));
                  }
                }}
              />
            </div>

            <span className={questionConfigStyles.subtext}>questions</span>
          </div>
          {formErrors.maxQuestions && <p className={questionConfigStyles.invalid}>{formErrors.maxQuestions}</p>}

          {!isMaxLimitEnabled && (
            <p className={questionConfigStyles.questionDescription}>The default setting allows 1 question per viewer</p>
          )}
        </div>

        {!isMobile && (
          <div className={questionConfigStyles.questionModalActions}>
            <OButton text="Cancel" onClick={onQuestionsCancel} variant="primary" outlined size="x-small" />

            <OButton text="Done" onClick={onQuestionsDone} variant="primary" outlined size="x-small" />
          </div>
        )}
      </>
    </OResponsiveModal>
  );
};

export default QuestionConfigModal;
